/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Button, BoxSpinnerView, DatetimeText } from '@components/atoms'
import { DashboardMessage } from '@components/molecules'

import { useAppSelector, CrudType, ApiGetDataStatus } from '@redux/index'
import { DashboardDetailType } from '@schemas'
import { grey } from '@mui/material/colors'
import dayjs from 'dayjs'

interface PropsType {
	status: ApiGetDataStatus
	detail: DashboardDetailType
	onUpdateClick?: () => void
	onDeleteClick?: () => void
	showUpdate?: boolean
	showDelete?: boolean
	showComment?: boolean
	onAddMessageClick?: (str: string) => void
	onDeleteMessageClick?: (detailId: string, messageId: string) => void
	infoAccessory?: JSX.Element
	leftButtonAccessory?: JSX.Element
}

export const DashboardContent = (props: PropsType) => {
	const { detail, status, onUpdateClick, onDeleteClick } = props

	// redux state
	const crud: CrudType = useAppSelector(state => state.auth.currentAccess)

	//variable
	const showUpdate: boolean = props.showUpdate ?? true
	const showDelete: boolean = props.showDelete ?? true
	const showComment: boolean = props.showComment ?? true
	const createdAt = dayjs(detail.createdAt.split('.')[0]).format('YYYY-MM-DD HH:mm')
	const contents = decodeURI(detail.contents)

	return (
		<View css={style}>
			{['init', 'loading'].includes(status) ? (
				<BoxSpinnerView className="boxSpinnerView" />
			) : (
				<View className="dashboardContentOuter">
					<View className="detailInfoOuter">
						<View className="dashboardInfoView">
							<Text className="infoLabel" category="c2" hint="400">
								제목
							</Text>
							<Text className="submitText" category="s2" hint="500">
								{detail.title}
							</Text>
						</View>

						<View className="dashboardInfoView right">
							<Text className="infoLabel" category="c2" hint="400">
								조회수
							</Text>
							<Text className="submitText" category="s2" hint="500">
								{detail.viewsCount}
							</Text>
						</View>
					</View>

					<View className="detailInfoOuter mobile">
						<View className="dashboardInfoView">
							<Text className="infoLabel" category="c2" hint="400">
								조회수
							</Text>
							<Text className="submitText" category="s2" hint="500">
								{detail.viewsCount}
							</Text>
						</View>
					</View>

					<View className="detailInfoOuter">
						<View className="dashboardInfoView">
							<Text className="infoLabel" category="c2" hint="400">
								작성자
							</Text>
							<Text className="submitText" category="s2" hint="500">
								{detail.createdByName}
							</Text>
						</View>

						<View className="dashboardInfoView right">
							<Text className="infoLabel" category="c2" hint="400">
								등록일시
							</Text>
							<DatetimeText title={createdAt} color={grey[500]} />
						</View>
					</View>

					<View className="detailInfoOuter mobile">
						<View className="dashboardInfoView">
							<Text className="infoLabel" category="c2" hint="400">
								등록일시
							</Text>
							<DatetimeText title={createdAt} color={grey[500]} />
						</View>
					</View>

					{props.infoAccessory}

					<View>
						{['init', 'loading'].includes(status) ? (
							<BoxSpinnerView className="boxSpinnerView" />
						) : (
							<View className="dashboardContentsView">
								<View dangerouslySetInnerHTML={{ __html: contents }}></View>
							</View>
						)}
					</View>

					<View className="submitView">
						{props.leftButtonAccessory ? props.leftButtonAccessory : null}
						{showUpdate && crud.includes('U') && (
							<Button
								className="button"
								variant="contained"
								onClick={onUpdateClick ? onUpdateClick : () => {}}
							>
								수정
							</Button>
						)}
						{showDelete && crud.includes('D') && (
							<Button
								color="error"
								variant="contained"
								onClick={onDeleteClick ? onDeleteClick : () => {}}
							>
								삭제
							</Button>
						)}
					</View>
					{showComment && (
						<DashboardMessage
							status={status}
							dataList={detail.messages ?? []}
							onAddClick={props.onAddMessageClick}
							onDeleteClick={props.onDeleteMessageClick}
						/>
					)}
				</View>
			)}
		</View>
	)
}

const style = css({
	height: 'fit-content',
	overflow: 'hidden',

	'.dashboardContentOuter': {
		position: 'relative',
		height: '100%',
		overflow: 'auto',

		'.detailInfoOuter': {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			height: '3rem',
			padding: '0 0.5rem',
			borderBottom: '1px solid',
			borderColor: grey[300],

			'.dashboardInfoView': {
				display: 'flex',
				alignItems: 'center',
				minWidth: '65%',
				textAlign: 'left',

				'&.right': {
					minWidth: '35%',
				},

				'.infoLabel': {
					width: '3rem',
					marginRight: '0.8rem',
				},
			},

			'&.mobile': {
				display: 'none',
			},
		},

		'.dashboardContentsView': {
			width: '100%',
			height: 'fit-content',
			textAlign: 'left',
		},

		'.submitView': {
			display: 'flex',
			justifyContent: 'right',
			alignItems: 'center',
			height: '3rem',
			marginTop: '0.5rem',
			paddingTop: '0.5rem',
			borderTop: '1px solid',
			borderColor: grey[400],

			'.button': {
				marginRight: '0.5rem',
			},
		},
	},

	'.boxSpinnerView': {
		marginTop: '30%',
		height: '100%',
		overflow: 'auto',
	},

	'@media (max-width: 700px)': {
		'.dashboardContentOuter': {
			'.detailInfoOuter': {
				'.dashboardInfoView': {
					minWidth: '100%',

					'&.right': {
						display: 'none',
					},
				},

				'&.mobile': {
					display: 'block',
					paddingTop: '0.3rem',
				},
			},

			'.dashboardContentsView': {
				height: 'fit-content',
			},
		},
	},
})
